/**
 * The /word routes: questions with status, live answers for a push, push,
 * writing plan, status and first-time mapping. Every answer and writing-plan
 * write goes through mytender's collaboration service on the server, so the
 * web editor's live collaborators are never overwritten.
 */
import axios from "../helper/axios";
import { apiURL } from "../helper/urls";

export const SECTION_STATUSES = [
  "Not Started",
  "In Progress",
  "Awaiting review",
  "In review",
  "Finalising",
  "Complete",
] as const;
export type SectionStatus = (typeof SECTION_STATUSES)[number];

export type Question = {
  section_id: string;
  section_number: string;
  heading: string;
  question: string;
  status: SectionStatus;
  weighting?: string | null;
  word_count?: number | null;
  limit_value?: number | null;
  limit_type?: "words" | "pages" | "characters" | null;
  writingplan: string;
  subheadings: { title: string; word_count?: number | null }[];
  reviewer: string;
  answerer: string;
  answer_markdown: string;
  answer_hash: string;
  last_edited_by?: string | null;
  last_edited_at?: string | null;
};

export type LiveAnswer = {
  section_id: string;
  answer_markdown?: string;
  answer_hash?: string;
  pending_suggestions?: number;
  editors: string[];
  connections: number;
  live: boolean;
};

export type PushResult = {
  section_id: string;
  status: "pushed" | "skipped" | "failed";
  reason?: string;
  answer_hash?: string;
  status_changed_to?: SectionStatus;
};

const auth = (token: string) => ({ Authorization: `Bearer ${token}` });
const base = (bidId: string) => `word/bids/${encodeURIComponent(bidId)}`;

export const fetchQuestions = async (bidId: string, token: string, signal?: AbortSignal): Promise<Question[]> => {
  const { data } = await axios.get(apiURL(`${base(bidId)}/questions`), { headers: auth(token), signal });
  return Array.isArray(data?.questions) ? data.questions : [];
};

export const fetchLive = async (bidId: string, sectionIds: string[], token: string): Promise<LiveAnswer[]> => {
  if (!sectionIds.length) return [];
  const { data } = await axios.post(
    apiURL(`${base(bidId)}/questions/live`),
    { section_ids: sectionIds },
    { headers: auth(token) }
  );
  return Array.isArray(data?.questions) ? data.questions : [];
};

export const fetchAnswer = async (bidId: string, sectionId: string, token: string): Promise<LiveAnswer> => {
  const { data } = await axios.get(apiURL(`${base(bidId)}/questions/${encodeURIComponent(sectionId)}/answer`), {
    headers: auth(token),
  });
  return data;
};

export const pushQuestions = async (
  bidId: string,
  questions: { section_id: string; markdown: string }[],
  token: string
): Promise<PushResult[]> => {
  const { data } = await axios.post(
    apiURL(`${base(bidId)}/push`),
    { questions },
    { headers: auth(token), timeout: 120_000 }
  );
  return Array.isArray(data?.results) ? data.results : [];
};

export type OrgUser = { username: string; email: string; firstname?: string; lastname?: string };

/** The organisation's members, for choosing reviewers. */
export const fetchOrganisationUsers = async (token: string): Promise<OrgUser[]> => {
  const form = new FormData();
  form.append("include_pending", "false");
  const { data } = await axios.post(apiURL("/get_organization_users"), form, { headers: auth(token) });
  return Array.isArray(data)
    ? data.filter((u): u is OrgUser => Boolean(u?.username) && u.username !== "Request Pending")
    : [];
};

export type ReviewRequest = { reviewers: string[]; message: string; restrict_editing: boolean };
export type ReviewResult = { emailed: string[]; skipped: string[]; reviewer: string };

/** Send a question for review: reviewers set, Awaiting review, reviewers emailed. */
export const sendForReview = async (
  bidId: string,
  sectionId: string,
  request: ReviewRequest,
  token: string
): Promise<ReviewResult> => {
  const { data } = await axios.post(
    apiURL(`${base(bidId)}/questions/${encodeURIComponent(sectionId)}/send_for_review`),
    request,
    { headers: auth(token) }
  );
  return { emailed: data?.emailed ?? [], skipped: data?.skipped ?? [], reviewer: data?.reviewer ?? "" };
};

export const saveLimit = async (
  bidId: string,
  sectionId: string,
  limit: { limit_value: number | null; limit_type: "words" | "pages" | "characters" },
  token: string
): Promise<{ limit_value: number | null; limit_type: "words" | "pages" | "characters"; word_count: number }> => {
  const { data } = await axios.post(apiURL(`${base(bidId)}/questions/${encodeURIComponent(sectionId)}/limit`), limit, {
    headers: auth(token),
  });
  return data;
};

export const saveWritingPlan = async (
  bidId: string,
  sectionId: string,
  writingplan: string,
  token: string
): Promise<void> => {
  await axios.post(
    apiURL(`${base(bidId)}/questions/${encodeURIComponent(sectionId)}/writing_plan`),
    { writingplan },
    { headers: auth(token) }
  );
};

export const setQuestionStatus = async (
  bidId: string,
  sectionId: string,
  status: SectionStatus,
  token: string
): Promise<{ emailed: string | null }> => {
  const { data } = await axios.post(
    apiURL(`${base(bidId)}/questions/${encodeURIComponent(sectionId)}/status`),
    { status },
    { headers: auth(token) }
  );
  return { emailed: data?.emailed ?? null };
};

export const startReview = async (bidId: string, sectionId: string, token: string): Promise<boolean> => {
  const { data } = await axios.post(
    apiURL(`${base(bidId)}/questions/${encodeURIComponent(sectionId)}/start_review`),
    {},
    { headers: auth(token) }
  );
  return Boolean(data?.changed);
};

export type MappingMatch = { section_id: string; start: number; end: number };

export const mapQuestions = async (
  bidId: string,
  paragraphs: { index: number; text: string; level: number | null }[],
  token: string
): Promise<MappingMatch[]> => {
  const { data } = await axios.post(
    apiURL(`${base(bidId)}/map_questions`),
    { paragraphs: paragraphs.map((p) => ({ ...p, level: p.level ?? undefined })) },
    { headers: auth(token), timeout: 90_000 }
  );
  return Array.isArray(data?.matches) ? data.matches : [];
};

/** The web app's link to one question in the bid's write view. */
export const questionLink = (bidId: string, sectionId: string): string => {
  const root = (process.env.REACT_APP_WEB_APP_URL || "https://app.mytender.io").replace(/\/+$/, "");
  const params = new URLSearchParams({ id: bidId, tab: "proposal-preview", sectionId });
  return `${root}/bid?${params.toString()}`;
};
